import { UUIDUtils } from 'app/utils/uuid.utils';

/**
 * Storage primitives for the menu analytics instrumentation.
 *
 * <p>Deliberately free of Angular DI. {@link captureAnalyticsExclusionFlags} is called from
 * `app.config.ts` during boot, and instantiating the analytics service there would arm listeners
 * and read storage on the render-blocking path. The service itself is armed lazily, at the first
 * idle moment after a menu page is displayed, or on the visitor's first gesture if that comes
 * first.
 *
 * <p>Every storage access is wrapped in a try/catch: Safari private mode throws on
 * `sessionStorage`, and analytics must never produce a visible trace on the public menu.
 */

/** Exclusion flags captured at boot, kept for the whole tab session. */
const EXCLUSION_KEY = 'qrsto_an_x';

/** Prefix of the per-establishment session state key. */
export const SESSION_KEY_PREFIX = 'qrsto_an_s_';

/** Batches handed to the network but not yet confirmed. */
export const OUTBOX_KEY = 'qrsto_an_o';

/** Mirror of the live buffer, for gestures not yet turned into a batch. */
export const BUFFER_KEY = 'qrsto_an_b';

/**
 * Exclusion decision carried by the tab, captured from the address at boot.
 *
 * <p>`skip` used to be read once in `app.config.ts` and kept nowhere: it vanished from the address
 * on the first internal navigation and the restaurateur's own browsing was counted as customer
 * traffic. Persisting it is the fix.
 */
export interface AnalyticsExclusion {
    /** URL identifier (slug or uuid) the flags were captured for. */
    id: string;
    /**
     * Both identifiers of the same establishment, uuid and slug, learnt once it has loaded. A QR
     * code opens the uuid address and the slug then replaces it, so the flags must hold under
     * either form within the same tab.
     */
    aliases?: string[];
    /** The `skip` query parameter. */
    excluded: boolean;
    /** The `previewToken` query parameter. Its mere presence excludes the session. */
    token?: string;
}

/**
 * Reads one key from `sessionStorage`, never throwing.
 */
export function safeSessionGet(key: string): string | null {
    try {
        return window.sessionStorage.getItem(key);
    } catch {
        return null;
    }
}

/**
 * Writes one key to `sessionStorage`, never throwing.
 */
export function safeSessionSet(key: string, value: string): void {
    try {
        window.sessionStorage.setItem(key, value);
    } catch {
        // Private mode, quota, disabled storage: analytics degrade silently.
    }
}

/**
 * Removes one key from `sessionStorage`, never throwing.
 */
export function safeSessionRemove(key: string): void {
    try {
        window.sessionStorage.removeItem(key);
    } catch {
        // Ignored on purpose.
    }
}

/**
 * Reads and parses a JSON value from `sessionStorage`. Returns null on anything unexpected.
 */
export function readJson<T>(key: string): T | null {
    const raw = safeSessionGet(key);
    if (!raw) {
        return null;
    }
    try {
        return JSON.parse(raw) as T;
    } catch {
        return null;
    }
}

/**
 * Serializes a value into `sessionStorage`. Never throws, never reports.
 */
export function writeJson(key: string, value: unknown): void {
    try {
        safeSessionSet(key, JSON.stringify(value));
    } catch {
        // Cyclic value or quota: nothing to do, and nothing to show.
    }
}

/**
 * Captures the `skip` and `previewToken` parameters and persists them for the whole tab session.
 *
 * <p>Called from `app.config.ts`, the riskiest file of the application: an exception there leaves
 * customers on the splash screen. Everything below is therefore wrapped, synchronous and
 * sub-millisecond — two storage accesses and no network.
 *
 * <p>The flags are sticky: once set for an identifier they survive every internal navigation and
 * every reload of the tab, under the uuid as well as the slug of the establishment, which is the
 * whole point. Flags belonging to another establishment are dropped rather than inherited.
 *
 * @param identifier first path segment, the establishment slug or uuid
 * @param search     raw `window.location.search`
 * @returns whether the tab is excluded for this establishment, by `skip` in the address or by an
 *          earlier load of the same tab. The visit counter must be told the same thing, so that
 *          both counters follow one rule.
 */
export function captureAnalyticsExclusionFlags(identifier: string | null, search: string): boolean {
    try {
        const params = new URLSearchParams(search || '');
        const skip = params.get('skip') === 'true';
        const token = params.get('previewToken') || undefined;
        const id = (identifier || '').toLowerCase();

        const stored = readJson<AnalyticsExclusion>(EXCLUSION_KEY);
        const aliases = stored && Array.isArray(stored.aliases) ? stored.aliases : [];
        const inherited = stored && (stored.id === id || aliases.indexOf(id) !== -1) ? stored : null;
        const excluded = skip || !!(inherited && inherited.excluded);

        writeJson(EXCLUSION_KEY, {
            id,
            aliases: inherited ? aliases : undefined,
            excluded,
            token: token || (inherited ? inherited.token : undefined),
        } as AnalyticsExclusion);
        return excluded;
    } catch {
        // Boot path: never throw, never block, never await.
        return false;
    }
}

/**
 * Records both identifiers of the establishment once it has loaded, so that flags captured under
 * one form of the address hold under the other within the same tab.
 *
 * <p>Called from `app.config.ts` once the establishment is known. Same constraints as
 * {@link captureAnalyticsExclusionFlags}: synchronous, wrapped, never throws.
 *
 * @param uuid public uuid of the loaded establishment
 * @param slug slug of the loaded establishment, when it has one
 */
export function bindAnalyticsExclusionAliases(uuid: string | null | undefined, slug: string | null | undefined): void {
    try {
        const stored = readJson<AnalyticsExclusion>(EXCLUSION_KEY);
        if (!stored) {
            return;
        }
        const ids = [uuid, slug]
            .map((value) => (value || '').toLowerCase())
            .filter((value, index, all) => !!value && all.indexOf(value) === index);
        if (ids.indexOf(stored.id) === -1) {
            // Flags captured for another establishment are never extended to this one.
            return;
        }
        writeJson(EXCLUSION_KEY, { ...stored, aliases: ids } as AnalyticsExclusion);
    } catch {
        // Boot path: never throw, never block, never await.
    }
}

/**
 * Reads back the exclusion flags captured at boot.
 */
export function readAnalyticsExclusion(): AnalyticsExclusion | null {
    return readJson<AnalyticsExclusion>(EXCLUSION_KEY);
}

/**
 * Draws a random session identifier.
 *
 * <p>It must NEVER be derived from a device signal. The existing `qrsto_fp` browser fingerprint
 * and its `X-Fingerprint` header are explicitly not reused here: that value is a full tracker
 * subject to consent, its purpose is the contest anti-fraud check, and its cookie is set at the
 * domain root and is therefore shared by every restaurant.
 */
export function newSessionId(): string {
    try {
        const webCrypto: any = typeof crypto !== 'undefined' ? crypto : null;
        if (webCrypto && typeof webCrypto.randomUUID === 'function') {
            return webCrypto.randomUUID();
        }
    } catch {
        // Fall through to the existing uuid utility.
    }
    return UUIDUtils.generate();
}
